import PropTypes from 'prop-types';
import IncidentCard from '../IncidentCard';
import { calculateRiskScore, calculateTrend } from '@/utils/riskScoring';

/**
 * Insider Risk View Component for SOC Dashboard
 * Displays insider risk indicators with detailed breakdown
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Insider risk data
 * @param {Object} props.previousData - Previous period data for trend calculation
 */
const InsiderRiskView = ({ data, previousData }) => {
  if (!data) {
    return (
      <div className="soc-view insider-risk-view">
        <h2 className="view-title">Insider Risk Detection</h2>
        <p className="no-data-message">Loading insider risk data...</p>
      </div>
    );
  }

  // Calculate risk scores for each insider risk category
  const anomalousAccessScore = calculateRiskScore({
    fileCount: data.insiderAnomalousAccessCount || 0,
    offHoursActivityCount: data.insiderAnomalousAccessOffHours || 0,
    uniqueIPCount: data.insiderAnomalousAccessUniqueIPs || 0
  });

  const privilegeEscalationScore = calculateRiskScore({
    fileCount: data.insiderPrivilegeEscalationCount || 0,
    failedAttempts: data.insiderPrivilegeEscalationFailed || 0
  });

  const offHoursScore = calculateRiskScore({
    fileCount: data.insiderOffHoursCount || 0,
    offHoursActivityCount: data.insiderOffHoursCount || 0,
    dataVolumeMB: data.insiderOffHoursDataVolume || 0
  });

  const suspiciousDownloadsScore = calculateRiskScore({
    fileCount: data.insiderSuspiciousDownloadsCount || 0,
    dataVolumeMB: data.insiderSuspiciousDownloadsVolume || 0
  });

  // Calculate trends
  const anomalousAccessTrend = previousData ? calculateTrend(
    data.insiderAnomalousAccessCount || 0,
    previousData.insiderAnomalousAccessCount || 0
  ) : null;

  const privilegeEscalationTrend = previousData ? calculateTrend(
    data.insiderPrivilegeEscalationCount || 0,
    previousData.insiderPrivilegeEscalationCount || 0
  ) : null;

  const offHoursTrend = previousData ? calculateTrend(
    data.insiderOffHoursCount || 0,
    previousData.insiderOffHoursCount || 0
  ) : null;

  const suspiciousDownloadsTrend = previousData ? calculateTrend(
    data.insiderSuspiciousDownloadsCount || 0,
    previousData.insiderSuspiciousDownloadsCount || 0
  ) : null;

  const totalInsiderIncidents = (data.insiderAnomalousAccessCount || 0) +
                                (data.insiderPrivilegeEscalationCount || 0) +
                                (data.insiderOffHoursCount || 0) +
                                (data.insiderSuspiciousDownloadsCount || 0);

  return (
    <div className="soc-view insider-risk-view">
      <h2 className="view-title">Insider Risk Detection</h2>

      {/* Summary Header */}
      <div className="view-summary-header">
        <div className="summary-stat">
          <span className="summary-label">Total Risk Indicators</span>
          <span className="summary-value">{totalInsiderIncidents}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">High-Risk Users</span>
          <span className="summary-value">{data.insiderHighRiskUsers || 0}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Active Investigations</span>
          <span className="summary-value">{data.insiderActiveInvestigations || 0}</span>
        </div>
      </div>

      {/* Insider Risk Incident Cards */}
      <div className="incident-cards-grid">
        <IncidentCard
          title="Anomalous Access Patterns"
          riskScore={anomalousAccessScore}
          data={{
            count: data.insiderAnomalousAccessCount || 0,
            offHoursEvents: data.insiderAnomalousAccessOffHours || 0,
            uniqueIPs: data.insiderAnomalousAccessUniqueIPs || 0,
            suspiciousLocations: data.insiderAnomalousAccessSuspiciousLocations || 0
          }}
          incidentType="InsiderRisk"
          trend={anomalousAccessTrend}
          expandable={true}
        />

        <IncidentCard
          title="Privilege Escalation Attempts"
          riskScore={privilegeEscalationScore}
          data={{
            count: data.insiderPrivilegeEscalationCount || 0,
            failedAttempts: data.insiderPrivilegeEscalationFailed || 0,
            successfulEscalations: data.insiderPrivilegeEscalationSuccessful || 0,
            affectedSystems: data.insiderPrivilegeEscalationSystems || 0
          }}
          incidentType="InsiderRisk"
          trend={privilegeEscalationTrend}
          expandable={true}
        />

        <IncidentCard
          title="Off-Hours Activity"
          riskScore={offHoursScore}
          data={{
            count: data.insiderOffHoursCount || 0,
            dataAccessed: `${Math.round((data.insiderOffHoursDataVolume || 0) / 1024)} GB`,
            peakActivity: data.insiderOffHoursPeakTime || 'N/A',
            uniqueUsers: data.insiderOffHoursUniqueUsers || 0
          }}
          incidentType="InsiderRisk"
          trend={offHoursTrend}
          expandable={true}
        />

        <IncidentCard
          title="Suspicious Downloads"
          riskScore={suspiciousDownloadsScore}
          data={{
            count: data.insiderSuspiciousDownloadsCount || 0,
            totalVolume: `${Math.round((data.insiderSuspiciousDownloadsVolume || 0) / 1024)} GB`,
            sensitiveFiles: data.insiderSuspiciousDownloadsSensitive || 0,
            externalDestinations: data.insiderSuspiciousDownloadsExternal || 0
          }}
          incidentType="InsiderRisk"
          trend={suspiciousDownloadsTrend}
          expandable={true}
        />
      </div>

      {/* Risk User List */}
      <div className="view-details-section">
        <h3>High-Risk Users Monitor</h3>
        <div className="risk-users-list">
          {data.insiderTopRiskyUsers && data.insiderTopRiskyUsers.length > 0 ? (
            <table className="risk-users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Risk Score</th>
                  <th>Indicators</th>
                  <th>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {data.insiderTopRiskyUsers.slice(0, 5).map((user, idx) => (
                  <tr key={idx}>
                    <td>{user.username || 'Unknown'}</td>
                    <td className="risk-score-cell">
                      <span style={{ color: user.riskScore >= 70 ? '#dc3545' : user.riskScore >= 40 ? '#fd7e14' : '#ffc107' }}>
                        {user.riskScore || 0}
                      </span>
                    </td>
                    <td>{user.indicatorCount || 0}</td>
                    <td>{user.lastActivity ? new Date(user.lastActivity).toLocaleString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-data-message">No high-risk users identified</p>
          )}
        </div>
      </div>
    </div>
  );
};

InsiderRiskView.propTypes = {
  data: PropTypes.object,
  previousData: PropTypes.object
};

InsiderRiskView.defaultProps = {
  data: null,
  previousData: null
};

export default InsiderRiskView;
